/**
 * Thin fetch wrapper for API v1 (plan/doc/04): `X-Client: admin`, bearer auth,
 * envelope unwrapping, single-flight refresh on `token_expired`.
 * Phase 1 replaces the hand-written calls with an openapi-typescript client.
 */

export const API_BASE = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? "";
const V1 = `${API_BASE}/api/v1`;

const ACCESS_KEY = "admin.access_token";
const REFRESH_KEY = "admin.refresh_token";

export type ApiErrorBody = { error: { code: string; message: string; details?: unknown } };

export class ApiError extends Error {
  constructor(
    public readonly code: string,
    message: string,
    public readonly status: number,
    public readonly details?: unknown,
  ) {
    super(message);
  }
}

export const tokens = {
  get access() {
    return localStorage.getItem(ACCESS_KEY);
  },
  get refresh() {
    return localStorage.getItem(REFRESH_KEY);
  },
  set(access: string, refresh: string) {
    localStorage.setItem(ACCESS_KEY, access);
    localStorage.setItem(REFRESH_KEY, refresh);
  },
  clear() {
    localStorage.removeItem(ACCESS_KEY);
    localStorage.removeItem(REFRESH_KEY);
  },
};

let refreshing: Promise<boolean> | null = null;

async function refreshSession(): Promise<boolean> {
  if (refreshing) return refreshing;
  refreshing = (async () => {
    const refresh = tokens.refresh;
    if (!refresh) return false;
    const res = await fetch(`${V1}/auth/refresh`, {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-Client": "admin" },
      body: JSON.stringify({ refresh_token: refresh }),
    });
    if (!res.ok) {
      tokens.clear();
      return false;
    }
    const body = (await res.json()) as { data: { access_token: string; refresh_token: string } };
    tokens.set(body.data.access_token, body.data.refresh_token);
    return true;
  })().finally(() => {
    refreshing = null;
  });
  return refreshing;
}

export interface RequestOptions {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined>;
  auth?: boolean;
  retry?: boolean;
}

export async function api<T>(path: string, opts: RequestOptions = {}): Promise<{ data: T; meta?: PageMeta }> {
  const { method = "GET", body, query, auth = true, retry = true } = opts;
  const url = new URL(`${V1}${path}`, window.location.origin);
  for (const [k, v] of Object.entries(query ?? {})) {
    if (v !== undefined) url.searchParams.set(k, String(v));
  }
  const headers: Record<string, string> = { Accept: "application/json", "X-Client": "admin" };
  if (body !== undefined) headers["Content-Type"] = "application/json";
  if (auth && tokens.access) headers.Authorization = `Bearer ${tokens.access}`;

  const res = await fetch(url, { method, headers, body: body === undefined ? undefined : JSON.stringify(body) });
  if (res.status === 204) return { data: undefined as T };

  const text = await res.text();
  const json = text ? (JSON.parse(text) as unknown) : null;
  if (res.ok) return json as { data: T; meta?: PageMeta };

  const err = (json as ApiErrorBody | null)?.error;
  if (res.status === 401 && err?.code === "token_expired" && auth && retry) {
    if (await refreshSession()) return api<T>(path, { ...opts, retry: false });
  }
  throw new ApiError(err?.code ?? "unexpected", err?.message ?? res.statusText, res.status, err?.details);
}

export interface PageMeta {
  page: number;
  per_page: number;
  total: number;
}

export interface User {
  id: string;
  role: "admin" | "city_admin" | "manager" | "driver" | "client";
  name: string;
  email: string;
  phone: string | null;
  vendor_id: string | null;
  photo_url: string | null;
  is_active: boolean;
  created_at: string;
}

export interface AppConfig {
  app_name: string;
  company_name: string;
  currency: { code: string; symbol: string };
  country_code: string;
  colors: Record<string, string>;
  features: Record<string, unknown>;
  dispatch: Record<string, unknown>;
  /** Raster tile template for maps; null = OpenStreetMap. */
  map_tiles_url?: string | null;
  vendor_types: { id: string; name: string; slug: string; color: string | null }[];
}
